<template>
  <NotFound v-if="notFound" />
  <div class="container" ref="container" v-else>
    <b-loading
      :is-full-page="true"
      v-model="isLoading"
      :can-cancel="true"
    ></b-loading>
    <b-notification
      :closable="false"
      v-if="Object.keys(item).length === 0 && this.errorMessage"
      type="is-danger"
      has-icon
      class="my-8"
      >{{ $t("item.couldNotLoadItem") }}<br />{{
        this.errorMessage
      }}</b-notification
    >
    <div v-if="Object.keys(this.item).length > 0">
      <item-menu v-bind:item="item" />
      <div class="column px-0">
        <div>
          <FsLightbox
            ref="fsLightbox"
            :zoomIncrement="1.5"
            :exitFullscreenOnClose="true"
            :slideDistance="0.1"
            :slideshowTime="1500"
            :toggler="lightboxVisible"
            :slide="currentCarouselImage + 1"
            :sources="images.map(item => item.src)"
            :onSlideChange="lightBoxSlideChange"
            :onOpen="initSwipe"
          />
          <div ref="lightboxContainer"></div>
        </div>
        <div class="is-relative carousel-container">
          <b-carousel
            :arrow="true"
            :indicator="false"
            :arrow-both="false"
            :arrow-hover="false"
            :icon-size="'is-large'"
            :autoplay="false"
            :repeat="false"
            :has-drag="true"
            v-model="currentImage"
          >
            <b-carousel-item v-for="(item, i) in images" :key="i">
              <div
                class="image"
                @mousedown="startDrag"
                @mousemove="dragging"
                @mouseup="endDrag(i)"
                @click="handleClick(i)"
                @touchstart="startDrag"
                @touchmove="dragging"
                @touchend="endDrag(i)"
              >
                <img :src="item.src" />
              </div>
            </b-carousel-item>
          </b-carousel>
          <div class="carousel-overlay">
            <p>
              {{ item.images ? currentImage + 1 : 0 }} /
              {{ item.images ? item.images.length : 0 }}
            </p>
          </div>
        </div>
      </div>

      <div class="columns px-2 mx-0 is-multiline prospect">
        <div class="column is-8 px-1">
          <div class="user-toolbar mb-4">
            <!--<button class="button" @click="addFavorite()">
              <b-icon icon="heart" size="is-small"></b-icon>
              <span>Legg til som favoritt</span>
            </button>-->

            <vue-bottom-sheet-vue2
              :is-full-screen="true"
              :click-to-close="true"
              :overlay="true"
              :rounded="false"
              ref="sheet"
            >
              <addFavorite />
            </vue-bottom-sheet-vue2>

            <a
              class="ml-2"
              :title="$t('item.email')"
              :aria-label="$t('item.email')"
              :href="
                `mailto:?subject=${$t('item.tip')}: ${item.title}, ${
                  item.currentBid
                },-&amp;body=${$t(
                  'item.iFoundThis'
                )} stadssalg.no: https://stadssalg.no/items/${item.id}`
              "
              target="_blank"
            >
              <b-icon icon="envelope" size="is-large"></b-icon>
            </a>

            <a
              class="ml-1"
              :title="$t('item.shareOnFacebook')"
              :aria-label="$t('item.shareOnFacebook')"
              :href="
                `https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fstadssalg.no%2Fitems%2F${item.id}%3Ftaid%3DShare_ad_facebook`
              "
              target="_blank"
            >
              <b-icon pack="fab" icon="facebook" size="is-large"></b-icon>
            </a>
          </div>

          <p class="title mb-1">{{ item.title }}</p>
          <div class="mb-4"><b>ID:</b> {{ item.id }}<br /></div>
          <b-message icon="star" has-icon type="is-success" v-if="item.isWon">
            {{ $t("item.youWonThisItem") }}<br />{{ $t("item.seeMoreInfo") }}
            <router-link
              :to="{
                name: 'MyBids',
                query: {
                  bidId: item.bidId,
                  searchQuery: item.bidId
                }
              }"
              >{{ $t("item.seeMoreInfoLink") }}</router-link
            >
          </b-message>
          <b-message
            type="is-warning"
            v-else-if="!item.readyForSaleDate || item.readyForSaleDate == 0"
          >
            {{ $t("item.itemNotReadyForSale") }}
            <div class="mt-3 is-size-7">
              <div
                v-if="
                  getUserDocument?.settings.readyForSaleNotificationSMS ||
                    getUserDocument?.settings.readyForSaleNotificationEmail
                "
              >
                {{ $t("item.getNotificationOn") }}

                <b-taglist class="is-inline-block">
                  <b-tag
                    v-if="getUserDocument?.settings.readyForSaleNotificationSMS"
                    >{{ $t("item.sms") }}</b-tag
                  >
                  <b-tag
                    v-if="
                      getUserDocument?.settings.readyForSaleNotificationEmail
                    "
                    >{{ $t("item.email") }}</b-tag
                  ></b-taglist
                >
                {{ $t("item.getNotificationOnWhen") }}
              </div>
              <div v-else>
                {{ $t("item.getNotificationNone") }}
              </div>

              <br />
              <router-link :to="{ name: 'MySettings' }">
                {{ $t("item.manageYourNotifications") }}
              </router-link>
            </div>
          </b-message>
          <b-message type="is-success" v-else-if="item.readyForSaleDate > 0">
            {{ $t("item.readyForSale") }}
            <b>
              {{ getFormattedDateTime(item.readyForSaleDate, "DD.MM.YYYY") }}</b
            >
          </b-message>

          <div class="columns quick-info is-size-6 is-size-7-mobile is-mobile">
            <div class="column" v-if="hasKeyValue('Km-stand')">
              <div class="level">
                <div class="level-item key-data">
                  <span>{{ $t("item.mileage") }}</span>

                  <b-icon icon="tachometer-alt" />

                  <span> {{ getKeyValue("Km-stand") }} km </span>
                </div>
              </div>
            </div>
            <div class="column" v-if="hasKeyValue('Girkasse')">
              <div class="level">
                <div class="level-item key-data">
                  <span>{{ $t("item.transmission") }}</span>

                  <b-icon icon="cogs" />

                  <span>
                    {{ getKeyValue("Girkasse") }}
                  </span>
                </div>
              </div>
            </div>
            <div class="column" v-if="hasKeyValue('Drivstoff')">
              <div class="level">
                <div class="level-item key-data">
                  <span>{{ $t("item.fuelType") }}</span>

                  <b-icon icon="gas-pump" />

                  <span>
                    {{ getKeyValue("Drivstoff") }}
                  </span>
                </div>
              </div>
            </div>
            <div class="column" v-if="item.location">
              <div class="level">
                <div class="level-item key-data">
                  <span>{{ $t("item.location") }}</span>

                  <b-icon icon="map-marked" />

                  <span>
                    {{ item.location }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="item.category == 'Am I the Reason People Always Leave'">
            <div class="subtitle mb-1" v-if="item.category == 'Kjøretøy'">
              {{ $t("item.disclaimerPartOne") }}
            </div>
            <div class="my-2" v-if="item.category == 'Kjøretøy'">
              {{ $t("item.disclaimerPartTwo") }}
            </div>
          </div>

          <router-link
            :to="{ path: '/info/budsystemet' }"
            v-if="item.category == 'Kjøretøy'"
          >
            {{ $t("item.disclaimerReadMore") }}</router-link
          >
          <div class="subtitle is-4 mt-5 mb-2">
            {{ $t("item.description") }}
          </div>
          <div class="my-2" v-html="item.description"></div>

          <div class="subtitle is-4 mt-5 mb-2">
            {{ $t("item.documents") }}
          </div>

          <ul class="list" v-if="item.documents?.length">
            <li
              class=""
              v-for="(file, index) in item.documents"
              :key="`file-${index}`"
            >
              <b-icon icon="file"></b-icon>
              <a :href="mediaPath(file.url)">{{ file.name }}</a>
            </li>
          </ul>
          <div v-else>
            {{ $t("item.noDocuments") }}
          </div>

          <div v-if="Object.keys(technicalData).length != 0">
            <div class="subtitle is-4 mt-5 mb-2">
              {{ $t("item.information") }}
            </div>
            <b-message>
              {{ $t("item.publicDataDisclaimer") }}
            </b-message>

            <div class="columns is-multiline is-mobile">
              <div
                class="column is-half-mobile is-one-third-tablet"
                v-for="(val, key) in technicalData"
                :key="`technicalData-${key}`"
              >
                {{ $t(`item.technicalData['${key}']`) }} <br />
                <strong v-if="isDate(val)">{{
                  getFormattedDateTime(val, "DD.MM.YYYY")
                }}</strong>
                <strong v-else>
                  {{
                    $te(`item.technicalData.values['${val}']`)
                      ? $t(`item.technicalData.values['${val}']`)
                      : val
                  }}
                </strong>
              </div>
            </div>
          </div>

          <div v-if="item.equipment.length > 0">
            <div class="subtitle is-4 mt-5 mb-2">
              {{ $t("item.equipment") }}
            </div>

            <div class="columns is-multiline is-mobile">
              <div
                class="column is-half-mobile is-one-third-tablet"
                v-for="(equipment, index) in item.equipment"
                :key="`equipment-${index}`"
              >
                {{ equipment }}
              </div>
            </div>
          </div>

          <div class="subtitle is-4 mt-5 mb-2">
            {{ $t("item.expenses") }}
          </div>

          <table
            class="table is-fullwidth"
            v-if="
              item.orderLines?.filter(ordln => ordln.productId != 210).length >
                0
            "
          >
            <tbody>
              <tr
                v-for="(orderLine, index) in item.orderLines.filter(
                  ordln => ordln.productId != 210
                )"
                :key="`orderLine-${index}`"
              >
                <td class="is-capitalized">
                  {{ orderLine.comment }}
                </td>
                <td class="has-text-right">{{ orderLine.price }},-</td>
              </tr>

              <tr style="background: #333; color: #fff;">
                <td>
                  {{ $t("item.expensesTotal") }}
                </td>
                <td class="has-text-right">{{ summarizeOrderLines() }},-</td>
              </tr>
            </tbody>
          </table>
          <div v-else>
            {{ $t("item.noExpenses") }}
          </div>

          <div class="my-2" v-if="getMapUrl()">
            <div class="subtitle is-4 mt-5 mb-2">
              {{ $t("item.map") }}
            </div>
            <figure class="image is-16by9">
              <iframe
                class="has-ratio"
                width="640"
                height="360"
                frameborder="0"
                style="border:0"
                :src="getMapUrl()"
                allowfullscreen
              >
              </iframe>
            </figure>
          </div>
        </div>

        <div class="column is-4 mt-2" v-if="!item.isWon">
          <div class="is-divider" :data-content="$t('item.highestBid')"></div>

          <div class="subtitle is-6">{{ $t("item.highestBid") }}:</div>
          <div class="title is-2 mb-2 has-text-danger">
            {{ item.currentBid | numFormat("0,0") }}

            <b-tooltip :label="$t('item.youAreCurrentlyLeading')">
              <b-tag type="is-success" v-if="item.leadingAuction">{{
                $t("item.yourBid")
              }}</b-tag>
            </b-tooltip>
          </div>

          <b>{{ $t("item.location") }}:</b> {{ item.location }}<br />
          <b>{{ $t("item.minBid") }}:</b>
          {{ (item.currentBid + item.bidIncrement) | numFormat("0,0") }}<br />
          <b>{{ $t("item.minBidIncrement") }}:</b> {{ item.bidIncrement }}<br />
          <b>{{ $t("item.vat") }}:</b>
          {{ item.vat ? $t("item.vatIncluded") : $t("item.vatNone") }}
          <span v-if="item.percentageRate"
            ><br />
            <b>{{ $t("item.adminCommission") }}: </b>

            <b-tooltip
              type="is-dark"
              :label="`${item.percentageRate}% ${$t('item.ofBidAmount')}`"
            >
              {{ item.percentageRate }} &percnt;
            </b-tooltip>
            <p>
              {{ item.minPrice ? `${$t("item.min")} ${item.minPrice},-` : "" }}
              {{ item.maxPrice ? `${$t("item.max")} ${item.maxPrice},-` : "" }}
            </p></span
          >

          <div class="is-divider" :data-content="$t('item.giveBid')"></div>

          <b-message
            type="is-warning"
            v-if="this.getUser && this.getUser.emailVerified == false"
          >
            {{ $t("item.verifyEmailToBid") }}
            <p class="mt-2">
              <router-link :to="{ name: 'Profile' }">{{
                $t("item.openMyProfile")
              }}</router-link>
            </p>
          </b-message>
          <b-message
            type="is-danger"
            v-if="
              this.isUserAuth &&
                (getUserDocument?.phoneNumber == '' ||
                  getUserDocument?.phoneNumber == null)
            "
            >{{ $t("item.addValidPhoneNumberToBid") }}
            <p class="mt-2">
              <router-link :to="{ name: 'Profile' }">{{
                $t("item.openMyProfile")
              }}</router-link>
            </p></b-message
          >

          <b-message type="is-warning" v-if="this.isUserAuth == false">
            {{ $t("item.loginToBid") }}

            <div class="mt-5">
              <b-button
                @click="openLoginModal"
                :loading="isLoading"
                type="is-primary"
              >
                {{ $t("item.login") }}
              </b-button>
            </div>
          </b-message>
          <b-message
            type="is-link is-dark"
            v-if="this.getUserDocument && this.getUserDocument.companies.length"
          >
            <b-field :label="$t('item.giveBidBehalf')">
              <b-select expanded v-model="companyBid">
                <option value="" selected>{{
                  this.getUserDocument.displayName
                    ? this.getUserDocument.displayName
                    : this.getUserDocument.email
                }}</option>
                <option
                  v-for="company in this.getUserDocument.companies"
                  :value="company.companyId"
                  :key="company.companyId"
                >
                  {{ company.company.name }}</option
                >
              </b-select>
            </b-field>
            <router-link
              class=" is-pulled-right is-size-7"
              :to="{ name: 'MySettings' }"
            >
              {{ $t("item.changeBidderProfile") }}
            </router-link>
          </b-message>

          <b-message type="is-danger" v-if="checkIfDenied()">
            {{ $t("item.youAreDeniedToBid") }}
          </b-message>

          <form v-on:submit.prevent v-if="this.isUserAuth == true">
            <ValidationObserver ref="newBidRef" v-slot="valid">
              <ValidationProvider
                :rules="
                  `minBid:${item.currentBid +
                    (item.bidIncrement
                      ? item.bidIncrement
                      : 0)}|numeric|bidOnly500exceptMinBid:${item.currentBid +
                    item.bidIncrement},${Math.round(newBid / 500) * 500}`
                "
                v-slot="{ errors }"
              >
                <b-field
                  position="is-centered"
                  class="my-2 mt-5"
                  :message="errors[0]"
                  :type="{
                    'is-danger': errors[0]
                  }"
                >
                  <b-numberinput
                    type="is-success"
                    custom-class="has-text-right"
                    size="is-medium"
                    icon-right="gavel"
                    :controls="false"
                    controls-position="compact"
                    :controls-rounded="true"
                    :icon-right-clickable="false"
                    @change.native="checkAdmCost"
                    v-model="newBid"
                    value="`${Number(item.currentBid) + Number(item.bidIncrement)}`"
                  ></b-numberinput>
                </b-field>
              </ValidationProvider>

              <b-field position="is-centered" grouped>
                <b-checkbox v-model="toa"
                  >{{ $t("general.accept") }}
                  <router-link
                    target="_blank"
                    :to="{ path: '/info/budsystemet' }"
                    >{{ $t("item.terms") }}</router-link
                  ></b-checkbox
                >

                <b-button
                  :disabled="
                    toa == 0 ||
                      !valid.valid ||
                      newBid < item.currentBid + item.bidIncrement ||
                      (getUser && getUser.emailVerified == false) ||
                      isUserAuth == false ||
                      getUserDocument?.phoneNumber == '' ||
                      getUserDocument?.phoneNumber == null ||
                      checkIfDenied()
                  "
                  @click="makeBid"
                  type="is-primary"
                  >{{ $t("item.makeBid") }}</b-button
                >
              </b-field>
            </ValidationObserver>
          </form>

          <div class="is-divider" :data-content="$t('item.bidList')"></div>
          <div class="mb-5">
            <b-button @click="openBidList" type="is-info" expanded>
              {{ $t("item.openBidList") }}
            </b-button>
          </div>

          <!--  
          <div class="is-divider" data-content="Finansiering"></div>
          <div class="mb-5">
            
            <FinancialWidget
              v-if="item.currentBid"
              v-bind:item="item"
              v-bind:newBid="newBid"
            ></FinancialWidget>
            
          </div>
          -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import NotFound from "../../components/404.vue";
import FsLightbox from "fslightbox-vue/v2";
import Hammer from "hammerjs";
import helpers from "../../helpers";
import modalMixin from "../../mixins/modal";
//import FinancialWidget from "../financing/FinancialWidget.vue";
import AddFavorite from "./AddFavorite.vue";
import ItemMenu from "./ItemMenu.vue";
import Login from "../user/Login";
import BidList from "./BidList.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";

import Plausible from "plausible-tracker";

export default {
  components: {
    Login,
    NotFound,
    FsLightbox,
    ValidationObserver,
    ValidationProvider,
    //FinancialWidget,
    AddFavorite,
    ItemMenu,
    BidList
  },

  metaInfo() {
    return {
      title: this.item.title,
      meta: [
        { property: "fb:app_id", content: "190661696694" },
        { property: "og:site_name", content: "Stadssalg AS" },
        { property: "og:type", content: "article" },
        { property: "og:url", content: window.location.href },
        {
          property: "og:image",
          content: this.item.thumbnail
            ? this.mediaPath(this.item.thumbnail.url)
            : ""
        },
        { property: "og:image:width", content: "1500" },
        { property: "og:image:height", content: "1500" },
        { property: "og:locale", content: "nb_NO" },
        { property: "og:title", content: this.item?.title },
        {
          property: "og:description",
          content: this.item?.description?.replace(/(<([^>]+)>)/gi, "")
        }
      ]
    };
  },

  mixins: [helpers, modalMixin],

  data() {
    return {
      isDenied: null,
      notFound: false,
      isLoading: false,
      errorMessage: false,
      toa: 0,
      newBid: 0,
      companyBid: "",
      currentImage: 0,
      currentCarouselImage: null,
      gallery: false,
      lightboxVisible: false,
      isDragging: false,
      clickPrevented: false,
      item: {}
    };
  },

  computed: {
    ...mapGetters([
      "getUser",
      "getUserDocument",
      "isUserAuth",
      "getLoginStatus"
    ]),

    technicalData: function() {
      // we only want properties with value
      return window._.pickBy(this.item.technicalData, function(u) {
        if (u) {
          return u;
        } else {
          return false;
        }
      });
    },

    images: function() {
      if (this.item.images) {
        let arr = this.item.images.map((item, index) => {
          return {
            src: this.mediaPathResized(item.url, {
              width: 1800,
              quality: 75
            }),
            title: item.name,
            description: `${index + 1}/${this.item.images.length}`
          };
        });

        if (this.item.thumbnail) {
          /*let first = {
            src: this.mediaPath(this.item.thumbnail.url),
            title: this.item.thumbnail.name,
            description: ""
          }; */
          let first = arr.filter(
            item => item.src == this.mediaPath(this.item.thumbnail.url)
          );

          if (first.length > 0) {
            first = first[0];
            arr = arr.filter(item => item.src !== first.src);
            arr.unshift(first);
          }
        }

        return arr;
      } else {
        return [];
      }
    }
  },
  async mounted() {
    await this.fetchItem();

    // disable double tap zoom
    document.addEventListener(
      "dblclick",
      function(event) {
        event.preventDefault();
      },
      { passive: false }
    );

    /*
    // TODO (to get attention)
    // check if getUser is populated. This means, our watcher wont trigger and we have to do the axios call.
    if (this.getUser === null) {
      //this.fetchItem();
    } else {
      // this comp was just hard refreshed and our watcher will take care of the axios call.
      this.fetchItem();
    } */

    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("keydown", this.keydown);
  },
  /*
  beforeRouteUpdate(to, from, next) {
    this.getItem(to.params.item);
    //history.pushState({}, null, "/items/" + to.params.item);
    next();
  }, */

  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("keydown", this.keydown);
    this.removeLightbox();
  },

  methods: {
    removeLightbox() {
      document.documentElement.classList.remove("fslightbox-open");
    },
    initSwipe() {
      if (this.lightboxVisible) {
        const lightboxElement = document.querySelector(".fslightbox-container");

        if (lightboxElement) {
          this.hammer = new Hammer(lightboxElement);
          this.hammer
            .get("swipe")
            .set({ direction: Hammer.DIRECTION_VERTICAL });
          this.hammer.on("swipeup swipedown", this.dismissLightbox);
        }
      }
    },
    dismissLightbox() {
      //console.log("dismissing lightbox");
      this.currentCarouselImage = this.currentImage;
      this.lightboxVisible = false;
    },

    handleClickOutside(event) {
      if (
        this.lightboxVisible &&
        !this.$refs.fsLightbox.$el.contains(event.target)
      ) {
        //this.lightboxVisible = false;
      }
    },

    lightBoxSlideChange(lightbox) {
      const fsLightbox = this.$refs.fsLightbox;

      const naverComponent = fsLightbox.$children.find(
        child => child.$options._componentTag === "Naver"
      );

      const slideNumberComponent = naverComponent.$children.find(
        child => child.$options._componentTag === "SlideNumber"
      );

      if (slideNumberComponent) {
        this.currentImage = slideNumberComponent.slide - 1;
      }
    },

    startDrag() {
      this.isDragging = false;
      this.clickPrevented = false;
    },
    dragging() {
      this.isDragging = true;
      this.clickPrevented = true;
    },
    endDrag(i) {
      if (!this.isDragging) {
        this.clickPrevented = true;
      }
    },
    handleClick(i, event) {
      if (this.clickPrevented) {
        this.clickPrevented = false;
        return;
      }
      this.showImg(i);
    },

    showImg: function(index) {
      //console.log("show image", index);
      this.currentCarouselImage = index;
      this.lightboxVisible = !this.lightboxVisible;
    },

    checkIfDenied() {
      // check if we have a user
      if (!this.getUser) {
        return false;
      }

      if (this.isDenied !== null) {
        return this.isDenied;
      }

      // call to check if user is denied
      return this.$axios
        .get(`${window.apiURL}/isDenied`)
        .then(response => {
          if (response.data?.success === false) {
            this.isDenied = true;
            return true;
          } else {
            this.isDenied = false;
            return false;
          }
        })
        .catch(() => {});
    },
    openLoginModal() {
      this.$router.push("/login");
      //this.openModal({ component: Login, props: { name: "Login" } });
    },
    summarizeOrderLines() {
      return this.item.orderLines
        .filter(ordln => ordln.productId != 210)
        .reduce((total, obj) => obj.price + total, 0);
    },
    addColumn(text) {
      let cell = document.createElement("td");
      cell.appendChild(document.createTextNode(text));

      return cell;
    },
    makeBid() {
      this.checkAdmCost(false);
      let tbl = document.createElement("table");
      tbl.className =
        "table mb-3 costTable is-striped is-fullwidth is-bordered";
      let tblBody = document.createElement("tbody");
      let totalPrice = Number(this.newBid);
      let totalVat = 0;

      if (this.item.vat) {
        totalVat += totalPrice * (this.item.vat / 100);
      }

      // add bid line
      let row = document.createElement("tr");
      row.appendChild(this.addColumn("Ditt bud"));
      row.appendChild(this.addColumn("NOK " + this.formatNumber(this.newBid)));
      tblBody.appendChild(row);

      // add all the costLines
      if (this.item.orderLines) {
        this.item.orderLines
          .filter(elm => elm.productId === 210)
          .forEach(orderLine => {
            let price = 0;
            row = document.createElement("tr");

            row.appendChild(this.addColumn(orderLine.comment));
            price = orderLine.price;

            totalPrice += orderLine.price;
            if (this.item.vat) totalVat += orderLine.price * 0.25;

            row.appendChild(
              this.addColumn("NOK " + this.formatNumber(Math.ceil(price)))
            );
            tblBody.appendChild(row);
          });
      }

      // add MVA line
      if (this.item.vat) {
        row = document.createElement("tr");
        row.appendChild(this.addColumn("MVA"));
        row.appendChild(
          this.addColumn("NOK " + this.formatNumber(Math.ceil(totalVat)))
        );
        tblBody.appendChild(row);
      }

      // add summarized line
      row = document.createElement("tr");
      row.className = "has-text-weight-bold";
      row.appendChild(this.addColumn("Totalt"));
      row.appendChild(
        this.addColumn(
          "NOK " + this.formatNumber(Math.ceil(totalPrice + totalVat))
        )
      );
      tblBody.appendChild(row);

      totalPrice = Math.ceil(totalPrice + totalVat);

      tbl.appendChild(tblBody);

      let costTableHTML = tbl.outerHTML;

      this.$buefy.dialog.confirm({
        title: this.$t("item.giveBid"),
        message: `${this.$t("item.bindingBidQuestion")}:<br/> <b>${
          this.item.title
        }</b><p class="my-3"> <strong>${this.$t(
          "item.overview"
        )}</strong></p> ${costTableHTML} <p class="mt-2 is-size-7 is-uppercase">${this.numberToWords(
          totalPrice
        )} ${this.$t("item.norwegianKroner")}</p> <p class="mt-2">${this.$t(
          "item.bidCorrectQuestion"
        )}<br/>${this.$t("item.youCantChangeBid")}</p>`,
        confirmText: this.$t("item.giveBid"),
        cancelText: this.$t("general.cancel"),
        type: "is-danger",
        hasIcon: true,
        scroll: "keep",
        onConfirm: () => this.sendBid()
      });
    },

    sendBid() {
      this.isLoading = true;
      this.$axios
        .post(`${window.apiURL}/item/${this.$route.params.item}/bid`, {
          bid: this.newBid,
          companyBid: this.companyBid
        })
        .then(response => {
          this.isLoading = false;
          if (response.status == 200 && response.data.success) {
            this.$buefy.toast.open({
              type: "is-success",
              message: response.data.message
            });

            // track event plausible
            const { trackEvent } = Plausible();
            trackEvent("bidSuccess");

            this.item.currentBid = this.newBid;
            this.item.currentBidUserId = this.getUser.uid;
            if (this.item.bidIncrement === 0) {
              //this.item.bidIncrement = 1000;
            }
            this.newBid = this.newBid + this.item.bidIncrement;
            this.toa = false;
          } else {
            this.$buefy.toast.open({
              type: "is-warning",
              message: response.data.message
            });
          }
        })
        .catch(error => {
          this.isLoading = false;
          if (error.response) {
            this.$buefy.toast.open({
              type: "is-danger",
              message: error.response?.data?.message
            });
          } else {
            this.$buefy.toast.open({
              type: "is-danger",
              message: this.$t("item.bidError")
            });
          }
        });
    },

    fetchItem: async function() {
      await this.$axios
        .get(`${window.apiURL}/item/${this.$route.params.item}`)
        .then(response => {
          this.notFound = false;
          this.isLoading = false;
          this.hasLoaded = true;
          this.item = response.data;
          this.newBid = response.data.currentBid + response.data.bidIncrement;
          this.checkAdmCost(true);

          // lets set default bidder profile as well
          if (this.getUserDocument?.settings.defaultBidderProfile) {
            // check if this.getUserDocument.settings.defaultBidderProfile exists in this.getUserDocument.companies
            if (
              this.getUserDocument.companies.some(
                company =>
                  company.companyId ===
                  this.getUserDocument.settings.defaultBidderProfile
              )
            ) {
              this.companyBid = this.getUserDocument.settings.defaultBidderProfile;
            } else {
              this.companyBid = "";
            }
          }
        })
        .catch(error => {
          this.notFound = true;
          this.isLoading = false;
          //console.log(error);
          this.errorMessage = error.response?.data?.message
            ? error.response.data.message
            : error.message;
        });
    },

    keydown: function(e) {
      if (e.keyCode == 37) {
        document.querySelector(".has-icons-left").click();
      }
      if (e.keyCode == 39) {
        document.querySelector(".has-icons-right").click();
      }
    },

    closeCarousel: function(index) {
      this.currentCarouselImage = null;
      this.currentImage = index;
    },

    addFavorite() {
      this.openModal({
        component: AddFavorite,
        props: { name: "AddFavorite", itemId: this.item.id }
      });
    },

    hasKeyValue(key) {
      const result = window._.find(this.technicalData, function(val, name) {
        if (val && name) return name == key;
        else return false;
      });

      if (result) return true;
      return false;
    },

    getKeyValue(key) {
      return window._.find(this.technicalData, function(val, name) {
        if (val && name) return name == key;
        else return false;
      });
    },

    getMapUrl() {
      if (
        this.item.locationDetails?.address1 &&
        this.item.locationDetails?.city &&
        this.item.locationDetails?.zip
      ) {
        const queryString = `${this.item.locationDetails.address1} ${this.item.locationDetails.zip} ${this.item.locationDetails.city}`.replace(
          " ",
          "+"
        );

        return `https://www.google.com/maps/embed/v1/place?key=AIzaSyDdVhh6dCJw1aSbIRUbKFiGaUwEgkeCEo0&q=${queryString}`;
      } else {
        return false;
      }
    },

    isDynamicCommission() {
      let commission = false;

      this.item.orderLines?.forEach(orderLine => {
        if (orderLine.productId === 210) {
          commission = true;
        }
      });

      if (!commission && this.item.percentageRate) {
        return true;
      } else {
        return false;
      }
    },

    checkAdmCost(validation = false) {
      this.item.orderLines = this.item.orderLines?.filter(
        item => !item.isDynamic
      );
      if (this.isDynamicCommission()) {
        if (!validation) {
          validation = this.$refs.newBidRef.flags.valid;
        }

        if (
          this.item.minPrice &&
          this.item.maxPrice &&
          this.item.percentageRate &&
          validation
        ) {
          let fee =
            Number(this.newBid) * (Number(this.item.percentageRate) / 100);
          let admCost = fee;

          if (this.item.minPrice && fee < this.item.minPrice) {
            admCost = this.item.minPrice;
          } else if (this.item.maxPrice && fee >= this.item.maxPrice) {
            admCost = this.item.maxPrice;
          }

          this.item.orderLines = this.item.orderLines?.filter(
            elm => elm.productId !== 210
          );
          this.item.orderLines.push({
            isDynamic: true,
            comment: "Adm. gebyr",
            price: admCost,
            productId: 210
          });
        }
      }
    },

    openBidList() {
      this.openModal({
        component: BidList,
        props: { name: "BidList", itemId: this.item.id }
      });
    }
  },
  watch: {
    lightboxVisible(newValue) {
      //console.log("lightboxVisible changed", newValue);
      if (!newValue) {
        this.removeLightbox();
      }
    },
    getLoginStatus(newValue) {
      switch (newValue) {
        case "loggedIn":
          this.fetchItem();
          break;
        case "loggedOut":
          break;
        default:
          break;
      }
    }
  }
};
</script>

<style lang="scss">
$carousel-arrow-background: black;
$carousel-arrow-color: green;

.bidBtn {
  text-align: right;
}

.user-toolbar {
  display: flex;
  align-items: center;

  .icon {
    color: grayscale($color: #666);

    &:hover {
      color: $primary;
      transition: 1s;
    }

    svg {
      max-height: 32px;
    }
  }
}

.prospect {
  @media screen and (max-width: $tablet) {
    > :nth-child(1) {
      order: 2;
    }
    > :nth-child(2) {
      order: 1;
    }

    > :nth-child(3) {
      order: 3;
    }
  }
}

.quick-info {
  .column {
    padding-left: 0;
    padding-right: 0;
  }

  .key-data {
    flex-direction: column;
    text-align: center;

    .title {
      margin-bottom: 2px;
    }
  }
}

.vel-modal {
  background: rgba(0, 0, 0, 0.9) !important;
}
.vel-img {
  /*
  height: auto !important;
  width: 100vw !important;
  */
}

.carousel {
  display: block;
  overflow: hidden;
  position: relative;
  transform-style: preserve-3d;

  .image {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) scale(1.02);
    max-width: 100%;
    height: 380px;
    display: contents;
  }

  img {
    height: 711px;
    max-height: 711px;
    @media screen and (max-width: $tablet) {
      height: 380px;
      max-height: 380px;
    }

    object-fit: cover;
    width: auto;
    margin: 0 auto;

    /*width: auto !important;
    transform: scale(1.2); */
  }
}

.carousel-item {
  text-align: center;
}

.carousel-overlay {
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  right: 0;
  left: 0;
  bottom: 0;
  text-align: center;
}

.carousel-overlay p {
  margin-bottom: 10px;
  display: inline-block;
  padding: 10px;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
}
</style>
